import React, { ChangeEvent, useContext, useEffect, useState } from 'react'
import { NameDto } from '@/api/Common/Types'
import AddButton from '@/components/AddButton/AddButton'
import { DataCard, DataGrid } from '@/components/DataCard/DataCard'
import {
  FormCheckboxField,
  FormField,
  FormMultiSelectField,
  FormReadonlyField,
  FormSelectField,
} from '@/components/Form/FormField'
import { infoToast, successToast, ToastContext } from '@/components/Toast/Toast'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import {
  isPhoneVerificationProviderNone,
  PhoneVerificationProvider,
  TwoFactorVerificationProvider,
} from 'api/Clients/Types/CountryPreference'
import { CountryPreferenceProduct } from 'api/Clients/Types/CountryPreferenceProduct'
import {
  getProductDisplayEntries,
  normalizeProduct,
  normalizeProductsForSubmission,
  TickmillProductDto,
} from 'api/Clients/Types/TickmillProductDto'
import { UpdateCountryPreference } from 'api/Clients/Types/UpdateCountryPreference'
import { TickmillCompanyDto } from 'api/Identity/Types/Company'
import { WalletCurrencyDto } from 'api/Wallet/Types/WalletCurrencyDto'
import { Loading } from 'components/Loading'
import copy from 'copy-to-clipboard'
import { FormikErrors, FormikProps, withFormik } from 'formik'
import { Info } from 'lucide-react'
import { DomainName } from 'types/Marketing/SGLLinkGenerator.types'
import { FormSubmitValues, JiraTicketPattern } from 'utils/formValidation'

import ConfirmationModal from '../../../components/ConfirmationModal/ConfirmationModal'
import MoreButton from '../../../components/MoreButton/MoreButton'
import { CountryProductsField } from './CountryProductsField'
import { CountryPreference } from './types'
import { usePublicDomainsFetch } from './usePublicDomainsFetch'

enum WorldCheckScreeningType {
  SCREENING = 'Screening',
  ONGOING_SCREENING = 'Ongoing Screening',
}

const WORLD_CHECK_OPTIONS = [
  { id: 1, value: WorldCheckScreeningType.ONGOING_SCREENING },
  { id: 2, value: WorldCheckScreeningType.SCREENING },
]

enum TwoFactorProviderOption {
  Email = 'email',
  SmsVonage = 'sms-vonage',
  SmsTwilio = 'sms-twilio',
  Google = 'google',
}

const EMPTY_COMPANY = {
  id: 0,
  name: '',
  shortName: '',
  tickmillPublicDomain: null,
}

const TWO_FACTOR_PROVIDER_OPTIONS = [
  {
    name: 'Email',
    id: TwoFactorProviderOption.Email,
    rightAddon: (
      <Tooltip>
        <TooltipTrigger>
          <Info size={14} style={{ marginLeft: '0.25rem' }} />
        </TooltipTrigger>
        <TooltipContent side='right'>
          <p>
            DeLorean users have the option to enable Two-Factor Authentication (2FA) for clients
            accounts on a country specific basis. Please be aware that this action is irreversible.
            Once enabled for the country, 2FA cannot be disabled
          </p>
        </TooltipContent>
      </Tooltip>
    ),
  },
  {
    name: 'SMS - Vonage',
    id: TwoFactorProviderOption.SmsVonage,
  },
  {
    name: 'SMS - Twilio',
    id: TwoFactorProviderOption.SmsTwilio,
    disabled: true,
  },
  {
    name: 'Google Authenticator',
    id: TwoFactorProviderOption.Google,
  },
]

type TwoFactorAuthVerificationProviderValues = {
  email: boolean
  sms: {
    vonage: boolean
    twilio: boolean
  }
  google: boolean
}

const getSelectedTwoFactorProviderOptions = (
  providers: TwoFactorAuthVerificationProviderValues
): TwoFactorProviderOption[] => {
  return [
    providers.email && TwoFactorProviderOption.Email,
    providers.sms.vonage && TwoFactorProviderOption.SmsVonage,
    providers.sms.twilio && TwoFactorProviderOption.SmsTwilio,
    providers.google && TwoFactorProviderOption.Google,
  ].filter((option): option is TwoFactorProviderOption => Boolean(option))
}

const EditCountryFormUI: React.FC<FormikProps<CountryPreferencesFormValues> & OuterProps> = (
  props
) => {
  const { handleSubmit, errors, isSubmitting, values, data, setFieldValue, onboardingFlowTypes } =
    props

  const [addNewCompanyForm, setAddNewCompanyForm] = useState<CompanyProduct>()
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false)
  const setToast = useContext(ToastContext)
  const [entityToDelete, setEntityToDelete] = useState<{
    data: CompanyProduct | null
    visible: boolean
  }>({
    data: null,
    visible: false,
  })

  const [editCompany, setEditCompany] = useState<CompanyProduct | undefined>()
  const [worldCheckValue, setWorldCheckValue] = useState<boolean>(true)

  const getCurrentWorldCheckValue = () => {
    return worldCheckValue
      ? WorldCheckScreeningType.ONGOING_SCREENING
      : WorldCheckScreeningType.SCREENING
  }

  const isExpedited = addNewCompanyForm?.expedited
  const activeEditCompany = editCompany
    ? values.companyProducts.find(
        (companyProduct) => companyProduct.tickmillCompany.id === editCompany.tickmillCompany.id
      )
    : undefined
  const buildCompanyDraft = (
    overrides: Partial<CompanyProduct> = {},
    base: CompanyProduct | undefined = addNewCompanyForm
  ): CompanyProduct => ({
    forcedLink: base?.forcedLink ?? '',
    products: base?.products ?? [],
    expedited: base?.expedited ?? false,
    onboardingFlowType: base?.onboardingFlowType ?? onboardingFlowTypes[0],
    ongoingScreeningEnabled: base?.ongoingScreeningEnabled ?? false,
    tickmillCompany: base?.tickmillCompany ?? EMPTY_COMPANY,
    isHidden: base?.isHidden ?? false,
    sumsubVerificationRequired: base?.sumsubVerificationRequired ?? false,
    tickmillPublicDomainId: base?.tickmillPublicDomainId,
    ...overrides,
  })

  const updateCompanyProductState = (
    companyId: number,
    updater: (companyProduct: CompanyProduct) => CompanyProduct
  ) => {
    setFieldValue(
      'companyProducts',
      values.companyProducts.map((companyProduct) =>
        companyProduct.tickmillCompany.id === companyId ? updater(companyProduct) : companyProduct
      )
    )
  }

  useEffect(() => {
    if (editCompany && !activeEditCompany) {
      setEditCompany(undefined)
    }
  }, [activeEditCompany, editCompany])

  const handleWorldCheckChange = (event: ChangeEvent<HTMLSelectElement>) => {
    const selectedValue = event.target.value
    const isOngoingScreening = selectedValue === WorldCheckScreeningType.ONGOING_SCREENING
    setWorldCheckValue(isOngoingScreening)
  }

  const handleTwoFactorProvidersChange = (selectedValues?: string[] | number[]) => {
    const selectedProviders = new Set((selectedValues || []).map(String))
    const hasVonage = selectedProviders.has(TwoFactorProviderOption.SmsVonage)

    props.setFieldValue('twoFactorAuthVerificationProviders', {
      email: selectedProviders.has(TwoFactorProviderOption.Email),
      sms: {
        vonage: hasVonage,
        twilio: !hasVonage && selectedProviders.has(TwoFactorProviderOption.SmsTwilio),
      },
      google: selectedProviders.has(TwoFactorProviderOption.Google),
    })
  }

  useEffect(() => {
    if (values.preferredCompanyId) {
      props.setCurrencyOnTickmillCompany(values.preferredCompanyId)
    }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const { addPublicDomains, editPublicDomains, isLoading } = usePublicDomainsFetch(
    addNewCompanyForm?.tickmillCompany?.id,
    editCompany?.tickmillCompany?.id
  )

  const handleUpdateCompanyProduct = (
    companyId: number,
    products?: CountryPreferenceProduct[],
    isHidden?: boolean
  ) => {
    updateCompanyProductState(companyId, (companyProduct) => ({
      ...companyProduct,
      products: products ?? companyProduct.products,
      isHidden: isHidden ?? companyProduct.isHidden,
    }))
  }

  const handleUpdatedExpedited = (
    companyId: number,
    products?: CountryPreferenceProduct[],
    expedited?: boolean
  ) => {
    updateCompanyProductState(companyId, (companyProduct) => ({
      ...companyProduct,
      products: products ?? companyProduct.products,
      expedited: expedited ?? companyProduct.expedited,
    }))
  }

  const handleUpdateSumsub = (
    companyId: number,
    products?: CountryPreferenceProduct[],
    sumsub?: boolean
  ) => {
    updateCompanyProductState(companyId, (companyProduct) => ({
      ...companyProduct,
      products: products ?? companyProduct.products,
      sumsubVerificationRequired: sumsub ?? companyProduct.sumsubVerificationRequired,
    }))
  }

  const handleAddNewProduct = () => {
    if (!addNewCompanyForm) return

    const existingByEntity = values.companyProducts.find(
      (cp) => cp.tickmillCompany.id === addNewCompanyForm.tickmillCompany.id
    )

    if (existingByEntity) {
      setToast(infoToast('Company already exists. If you want to update, edit existing company.'))
      return
    }

    const companyProducts = values.companyProducts.filter(
      (cp) => cp.tickmillCompany.id !== addNewCompanyForm.tickmillCompany.id
    )

    if (addNewCompanyForm.products.length === 0) {
      setToast(infoToast('Please add at least one product.'))
      return
    }

    const newCompanyWithWorldCheck = {
      ...addNewCompanyForm,
      ongoingScreeningEnabled: worldCheckValue,
    }

    setFieldValue('companyProducts', [...companyProducts, newCompanyWithWorldCheck])
    setAddNewCompanyForm(undefined)
    setWorldCheckValue(true)
  }

  const valid = () => {
    const isEmptyProducts = values.companyProducts.find((x) => x.products.length === 0)

    if (isEmptyProducts) {
      setToast(
        infoToast(`Please add at least one product for ${isEmptyProducts.tickmillCompany.name}.`)
      )
      return false
    }

    return true
  }

  const handleDeleteConfirmation = (product: CompanyProduct) => {
    setEntityToDelete({ data: product, visible: true })
  }

  const handleDelete = () => {
    if (entityToDelete.data) {
      setFieldValue(
        'companyProducts',
        values.companyProducts.filter(
          (x) => x.tickmillCompany.id !== entityToDelete.data?.tickmillCompany.id
        )
      )
    }

    setEntityToDelete({ data: null, visible: false })
  }

  const handleFormSubmit = (event?: React.FormEvent<HTMLFormElement>) => {
    event?.preventDefault()

    if (!valid()) return

    props.setSubmitting(true)
    handleSubmit()
  }

  return (
    <>
      {entityToDelete.data && entityToDelete.visible && (
        <ConfirmationModal
          contentText='Are you sure to delete?'
          yesButtonText='Yes, delete'
          onYes={handleDelete}
          onCancel={() => setEntityToDelete({ visible: false, data: null })}
          visible={!!entityToDelete.data && entityToDelete.visible}
        />
      )}
      {isAddDialogOpen && (
        <Dialog open={isAddDialogOpen} onOpenChange={() => setIsAddDialogOpen(false)}>
          <DialogContent size='sm'>
            <DialogHeader>
              <DialogTitle>Add New Entity</DialogTitle>
            </DialogHeader>

            <FormSelectField
              label='Entity'
              options={props.tickmillCompanies}
              value={addNewCompanyForm?.tickmillCompany?.id ?? ''}
              onChange={(value) => {
                const company = props.tickmillCompanies.find(
                  (item) => item.id === parseInt(String(value), 10)
                )

                if (!company) return

                setAddNewCompanyForm(
                  buildCompanyDraft({
                    tickmillCompany: { ...company, tickmillPublicDomain: null },
                  })
                )
              }}
            />

            <FormSelectField
              showLoading={isLoading}
              label='Public Domain'
              options={(addPublicDomains || []).map((domainName) => ({
                id: domainName.id,
                name: domainName.name,
              }))}
              value={addNewCompanyForm?.tickmillPublicDomainId}
              onChange={(value) => {
                const publicDomain =
                  addPublicDomains?.find((item) => item.id === Number(value)) || null

                setAddNewCompanyForm(
                  buildCompanyDraft({
                    tickmillCompany: {
                      ...(addNewCompanyForm?.tickmillCompany ?? EMPTY_COMPANY),
                      tickmillPublicDomain: publicDomain,
                    },
                    tickmillPublicDomainId: value ? Number(value) : undefined,
                  })
                )
              }}
            />

            <CountryProductsField
              availableProducts={props.tickmillProducts}
              products={addNewCompanyForm?.products || []}
              onChange={(updatedProducts) =>
                setAddNewCompanyForm(
                  buildCompanyDraft({
                    products: updatedProducts,
                  })
                )
              }
            />

            <FormSelectField
              label='Onboarding Type'
              value={addNewCompanyForm?.onboardingFlowType.id}
              options={onboardingFlowTypes}
              onChange={(value) => {
                const onboardingType =
                  onboardingFlowTypes?.find((item) => item.id === Number(value)) || null

                setAddNewCompanyForm(
                  buildCompanyDraft({
                    onboardingFlowType: onboardingType as NameDto,
                  })
                )
              }}
            />

            <FormSelectField
              label='WorldCheck'
              value={getCurrentWorldCheckValue()}
              options={WORLD_CHECK_OPTIONS.map((option) => ({
                id: option.value,
                name: option.value,
              }))}
              disabled={!isExpedited}
              onChange={(value) =>
                handleWorldCheckChange({
                  target: { value: String(value) },
                } as ChangeEvent<HTMLSelectElement>)
              }
            />

            <div className='grid gap-3 md:col-span-2 sm:grid-cols-3'>
              <FormCheckboxField
                label='Hidden'
                value={addNewCompanyForm?.isHidden}
                onChange={(checked) => {
                  setAddNewCompanyForm(
                    buildCompanyDraft({
                      isHidden: checked,
                    })
                  )
                }}
              />

              <FormCheckboxField
                label='Sumsub'
                value={addNewCompanyForm?.sumsubVerificationRequired}
                onChange={(checked) => {
                  setAddNewCompanyForm(
                    buildCompanyDraft({
                      sumsubVerificationRequired: checked,
                    })
                  )
                }}
              />

              <FormCheckboxField
                label='Expedited'
                value={addNewCompanyForm?.expedited}
                onChange={(checked) => {
                  setAddNewCompanyForm(
                    buildCompanyDraft({
                      expedited: checked,
                    })
                  )
                }}
              />
            </div>

            <DialogFooter>
              <Button onClick={handleAddNewProduct} disabled={isSubmitting} key='button'>
                Add Entity
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      {activeEditCompany && (
        <Dialog open={!!activeEditCompany} onOpenChange={() => setEditCompany(undefined)}>
          <DialogContent size='md'>
            <DialogHeader>
              <DialogTitle>{`Editing Entity: ${activeEditCompany.tickmillCompany.name}`}</DialogTitle>
              <DialogDescription>
                Adjust the entity configuration below. Changes stay local until you save the
                country.
              </DialogDescription>
            </DialogHeader>

            <FormReadonlyField
              label='Entity'
              value={activeEditCompany.tickmillCompany.name ?? ''}
            />

            <Loading isLoading={isLoading}>
              <FormSelectField
                label='Public Domain'
                options={(editPublicDomains || []).map((domainName) => ({
                  id: domainName.id,
                  name: domainName.name,
                }))}
                value={activeEditCompany.tickmillPublicDomainId}
                onChange={(value) => {
                  const publicDomain =
                    editPublicDomains?.find((domainName) => domainName.id === Number(value)) || null

                  updateCompanyProductState(
                    activeEditCompany.tickmillCompany.id,
                    (companyProduct) => ({
                      ...companyProduct,
                      tickmillPublicDomainId: value ? Number(value) : undefined,
                      tickmillCompany: {
                        ...companyProduct.tickmillCompany,
                        tickmillPublicDomain: publicDomain,
                      },
                    })
                  )
                }}
              />
            </Loading>

            <CountryProductsField
              className='md:col-span-2'
              availableProducts={props.tickmillProducts}
              products={activeEditCompany.products}
              onChange={(updatedProducts) =>
                handleUpdateCompanyProduct(activeEditCompany.tickmillCompany.id, updatedProducts)
              }
            />

            <FormSelectField
              label='Onboarding Type'
              value={activeEditCompany.onboardingFlowType.id}
              options={onboardingFlowTypes}
              onChange={(value) => {
                const onboardingType =
                  onboardingFlowTypes?.find((item) => item.id === Number(value)) || null

                updateCompanyProductState(
                  activeEditCompany.tickmillCompany.id,
                  (companyProduct) => ({
                    ...companyProduct,
                    onboardingFlowType: onboardingType as NameDto,
                  })
                )
              }}
            />

            <FormSelectField
              label='WorldCheck'
              value={
                activeEditCompany.ongoingScreeningEnabled
                  ? WorldCheckScreeningType.ONGOING_SCREENING
                  : WorldCheckScreeningType.SCREENING
              }
              options={WORLD_CHECK_OPTIONS.map((option) => ({
                id: option.value,
                name: option.value,
              }))}
              disabled={!activeEditCompany.expedited}
              onChange={(value) => {
                updateCompanyProductState(
                  activeEditCompany.tickmillCompany.id,
                  (companyProduct) => ({
                    ...companyProduct,
                    ongoingScreeningEnabled: value === WorldCheckScreeningType.ONGOING_SCREENING,
                  })
                )
              }}
            />

            <div className='grid gap-3 md:col-span-2 sm:grid-cols-3'>
              <FormCheckboxField
                label='Hidden'
                value={activeEditCompany.isHidden}
                onChange={(checked) =>
                  handleUpdateCompanyProduct(
                    activeEditCompany.tickmillCompany.id,
                    undefined,
                    checked
                  )
                }
              />

              <FormCheckboxField
                label='Sumsub'
                value={activeEditCompany.sumsubVerificationRequired}
                onChange={(checked) =>
                  handleUpdateSumsub(activeEditCompany.tickmillCompany.id, undefined, checked)
                }
              />

              <FormCheckboxField
                label='Expedited'
                value={activeEditCompany.expedited}
                onChange={(checked) =>
                  handleUpdatedExpedited(activeEditCompany.tickmillCompany.id, undefined, checked)
                }
              />
            </div>

            <DialogFooter>
              <Button variant='outline' onClick={() => setEditCompany(undefined)}>
                Done Editing
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      <Dialog open={props.isOpen} onOpenChange={(open) => !open && props.onClose()}>
        <DialogContent size='2xl' render={<form onSubmit={handleFormSubmit} />}>
          <DialogHeader>
            <DialogTitle>{`Edit Country: ${data.country.name}`}</DialogTitle>
          </DialogHeader>

          <Loading isLoading={props.isLoading ?? false}>
            <DataGrid simple columns={1}>
              <DataCard title='Country Preferences' colSpan={1} columns={2}>
                <FormReadonlyField label='Country' value={data.country.name ?? ''} />

                <FormSelectField
                  name='languageId'
                  label='Language'
                  options={props.languages}
                  error={errors.languageId}
                  onChange={(value) => props.setFieldValue('languageId', value ?? '')}
                />

                <FormSelectField
                  name='preferredCompanyId'
                  label='Preferred Tickmill Company'
                  options={props.tickmillCompanies}
                  error={errors.preferredCompanyId as string}
                  onChange={(value) => {
                    const id = value ? parseInt(String(value), 10) : 0
                    if (id) {
                      props.setCurrencyOnTickmillCompany(id)
                    }
                    props.setFieldValue('preferredCompanyId', value || null)
                  }}
                />

                <FormSelectField
                  name='currencyId'
                  label='Currency'
                  options={(props.currencies || []).map((currency) => ({
                    id: currency.id,
                    name: currency.id,
                  }))}
                  error={errors.currencyId}
                  onChange={(value) => props.setFieldValue('currencyId', value ?? '')}
                />

                <FormField
                  name='jiraTicketNumber'
                  label='Jira Ticket Number *'
                  error={errors.jiraTicketNumber}
                />

                <FormSelectField
                  options={[
                    { name: 'Vonage', id: PhoneVerificationProvider.VONAGE },
                    { name: 'Twilio', id: PhoneVerificationProvider.TWILIO },
                  ]}
                  allowClear
                  label='Phone Verification Provider'
                  name='phoneVerificationProviderId'
                />

                <FormMultiSelectField
                  name='twoFactorAuthVerificationProviders'
                  label='2FA Providers'
                  options={TWO_FACTOR_PROVIDER_OPTIONS}
                  value={getSelectedTwoFactorProviderOptions(
                    values.twoFactorAuthVerificationProviders
                  )}
                  allowClear={false}
                  onChange={handleTwoFactorProvidersChange}
                />
              </DataCard>

              <DataCard
                title='Configured Entities'
                description='Review existing entity mappings here, then open a row to edit its full configuration in a dedicated section.'
                colSpan={1}
                columns={1}
              >
                <AddButton
                  text='Add Entity'
                  onClick={() => setIsAddDialogOpen(true)}
                  data-test='add-user-button'
                />
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Entity</TableHead>
                      <TableHead>Products</TableHead>
                      <TableHead>Public Domain</TableHead>
                      <TableHead>Onboarding</TableHead>
                      <TableHead>WorldCheck</TableHead>
                      <TableHead>Flags</TableHead>
                      <TableHead>Link</TableHead>
                      <TableHead className='w-16 text-right' />
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {values.companyProducts.length ? (
                      values.companyProducts.map((companyProduct) => {
                        const isEditing =
                          activeEditCompany?.tickmillCompany.id ===
                          companyProduct.tickmillCompany.id
                        const flags = [
                          companyProduct.isHidden ? 'Hidden' : null,
                          companyProduct.expedited ? 'Expedited' : null,
                          companyProduct.sumsubVerificationRequired ? 'Sumsub' : null,
                        ].filter((flag): flag is string => Boolean(flag))
                        const worldCheckLabel = companyProduct.expedited
                          ? companyProduct.ongoingScreeningEnabled
                            ? WorldCheckScreeningType.ONGOING_SCREENING
                            : WorldCheckScreeningType.SCREENING
                          : 'Not applicable'

                        return (
                          <TableRow
                            key={companyProduct.tickmillCompany.id}
                            className={isEditing ? 'bg-muted/30' : undefined}
                          >
                            <TableCell className='align-top'>
                              <div className='space-y-1'>
                                <div className='font-medium'>
                                  {companyProduct.tickmillCompany.name}
                                </div>
                              </div>
                            </TableCell>

                            <TableCell className='align-top'>
                              {companyProduct.products.length ? (
                                <div className='flex flex-wrap gap-1.5'>
                                  {getProductDisplayEntries(companyProduct.products).map(
                                    (productEntry) => (
                                      <Badge
                                        key={`${companyProduct.tickmillCompany.id}-${productEntry}`}
                                        variant='neutral'
                                        className='h-auto min-h-6 max-w-full items-start overflow-visible whitespace-normal wrap-break-word py-1 text-left leading-tight'
                                      >
                                        {productEntry}
                                      </Badge>
                                    )
                                  )}
                                </div>
                              ) : (
                                <span className='text-muted-foreground'>No products</span>
                              )}
                            </TableCell>

                            <TableCell className='align-top'>
                              {companyProduct.tickmillCompany.tickmillPublicDomain?.name ? (
                                companyProduct.tickmillCompany.tickmillPublicDomain.name
                              ) : (
                                <span className='text-muted-foreground'>None</span>
                              )}
                            </TableCell>

                            <TableCell className='align-top'>
                              {companyProduct.onboardingFlowType.name}
                            </TableCell>

                            <TableCell className='align-top'>{worldCheckLabel}</TableCell>

                            <TableCell className='align-top'>
                              {flags.length ? (
                                <div className='flex flex-wrap gap-1.5'>
                                  {flags.map((flag) => (
                                    <Badge
                                      key={`${companyProduct.tickmillCompany.id}-${flag}`}
                                      variant='light'
                                    >
                                      {flag}
                                    </Badge>
                                  ))}
                                </div>
                              ) : (
                                <span className='text-muted-foreground'>No flags</span>
                              )}
                            </TableCell>

                            <TableCell className='align-top'>
                              {companyProduct.forcedLink ? (
                                <Button
                                  variant='link'
                                  size='sm'
                                  className='h-auto p-0'
                                  onClick={() => {
                                    copy(companyProduct.forcedLink)
                                    setToast(successToast('Link copied to clipboard'))
                                  }}
                                >
                                  Copy link
                                </Button>
                              ) : (
                                <span className='text-muted-foreground'>Unavailable</span>
                              )}
                            </TableCell>

                            <TableCell className='align-top text-right'>
                              <MoreButton
                                disabled={isSubmitting}
                                items={[
                                  {
                                    name: 'Edit',
                                    'data-test': 'edit',
                                    onClick: () => setEditCompany(companyProduct),
                                  },
                                  {
                                    name: 'Delete',
                                    'data-test': 'delete',
                                    onClick: () => handleDeleteConfirmation(companyProduct),
                                  },
                                ]}
                                data-test='more-button'
                              />
                            </TableCell>
                          </TableRow>
                        )
                      })
                    ) : (
                      <TableRow>
                        <TableCell colSpan={8} className='text-center text-muted-foreground'>
                          No configured entities yet.
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </DataCard>
            </DataGrid>
          </Loading>

          <DialogFooter>
            <Button
              disabled={isSubmitting || props.isLoading}
              onClick={props.onClose}
              variant='outline'
            >
              Cancel
            </Button>

            <Button disabled={isSubmitting || props.isLoading} key='button' type='submit'>
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  )
}

interface OuterProps {
  isOpen?: boolean
  onClose(): void
  isLoading?: boolean
  data: CountryPreference

  languages: NameDto<string>[]
  currencies: WalletCurrencyDto[]
  tickmillCompanies: TickmillCompanyDto[]
  onboardingFlowTypes: NameDto[]
  tickmillProducts: TickmillProductDto[]

  setCurrencyOnTickmillCompany(tickmillCompanyId: number): void
  onSubmit(values: FormSubmitValues<UpdateCountryPreference>): void
}

const dataToSubmit = (
  values: CountryPreferencesFormValues
): FormSubmitValues<UpdateCountryPreference> => {
  const twoFactorAuthVerificationProviders: {
    twoFactorVerificationProviderId: number
    TwoFactorAuthTypeId: number
  }[] = []

  if (values.twoFactorAuthVerificationProviders.email) {
    twoFactorAuthVerificationProviders.push({
      twoFactorVerificationProviderId: 1,
      TwoFactorAuthTypeId: 1,
    })
  }

  if (values.twoFactorAuthVerificationProviders.sms.vonage) {
    twoFactorAuthVerificationProviders.push({
      twoFactorVerificationProviderId: 2,
      TwoFactorAuthTypeId: 2,
    })
  }

  if (values.twoFactorAuthVerificationProviders.sms.twilio) {
    twoFactorAuthVerificationProviders.push({
      twoFactorVerificationProviderId: 3,
      TwoFactorAuthTypeId: 2,
    })
  }

  if (values.twoFactorAuthVerificationProviders.google) {
    twoFactorAuthVerificationProviders.push({
      twoFactorVerificationProviderId: 1,
      TwoFactorAuthTypeId: 3,
    })
  }

  return {
    preferredCompanyId: values.preferredCompanyId,
    languageId: values.languageId,
    currencyId: values.currencyId,
    countryId: values.countryId,
    jiraTicketNumber: values.jiraTicketNumber,
    phoneVerificationRequired: !isPhoneVerificationProviderNone(values.phoneVerificationProviderId),
    companyProducts: values.companyProducts.map((x) => ({
      tickmillCompanyId: x.tickmillCompany.id,
      products: normalizeProductsForSubmission(x.products),
      isHidden: x?.isHidden ?? false,
      isExpedited: x?.expedited ?? false,
      onboardingFlowTypeId: x?.onboardingFlowType.id ?? 1,
      tickmillPublicDomainId: x?.tickmillPublicDomainId || null,
      sumsubVerificationRequired: x?.sumsubVerificationRequired ?? false,
      ongoingScreeningEnabled: x?.ongoingScreeningEnabled ?? false,
    })),
    phoneVerificationProviderId: values.phoneVerificationProviderId,
    twoFactorAuthVerificationProviders,
  }
}

interface CountryPreferencesFormValues {
  countryId: string
  currencyId: string
  languageId: string
  preferredCompanyId: number
  phoneVerificationRequired: boolean
  phoneVerificationProviderId: number
  companyProducts: CompanyProduct[]
  jiraTicketNumber: string | null
  twoFactorAuthVerificationProviders: TwoFactorAuthVerificationProviderValues
}

interface CompanyProduct {
  tickmillCompany: {
    id: number
    name: string | null
    shortName: string | null
    tickmillPublicDomain: DomainName | null
  }
  products: CountryPreferenceProduct[]
  forcedLink: string
  isHidden: boolean
  expedited: boolean
  sumsubVerificationRequired: boolean
  onboardingFlowType: NameDto
  ongoingScreeningEnabled: boolean
  tickmillPublicDomainId: number | undefined
}

export const EditCountryForm = withFormik<OuterProps, CountryPreferencesFormValues>({
  mapPropsToValues: ({ data: countryPreference }) => {
    return {
      countryId: countryPreference?.country?.id ?? '',
      currencyId: countryPreference?.currency?.id ?? '',
      languageId: countryPreference?.language?.id ?? '',
      preferredCompanyId: countryPreference?.preferredCompany?.id ?? '',
      phoneVerificationRequired: countryPreference?.phoneVerificationRequired ?? false,
      companyProducts: countryPreference.companies.map((x) => ({
        tickmillCompany: x.company,
        isHidden: x.isHidden,
        products: x.products.map(normalizeProduct),
        forcedLink: x.forcedLink,
        tickmillPublicDomainId: x.company.tickmillPublicDomain?.id || undefined,
        expedited: x.expedited,
        ongoingScreeningEnabled: x.ongoingScreeningEnabled ?? false,
        onboardingFlowType: x.onboardingFlowType,
        sumsubVerificationRequired: x.sumsubVerificationRequired || false,
      })),
      jiraTicketNumber: '',
      phoneVerificationProviderId: countryPreference.phoneVerificationProvider?.id || 0,
      twoFactorAuthVerificationProviders: {
        google: !!countryPreference.twoFactorAuthVerificationProviders.find(
          (p) => p.twoFactorAuthType.id === TwoFactorVerificationProvider.Google
        ),
        email: !!countryPreference.twoFactorAuthVerificationProviders.find(
          (p) => p.twoFactorAuthType.id === TwoFactorVerificationProvider.Email
        ),
        sms: {
          vonage:
            countryPreference.twoFactorAuthVerificationProviders.find(
              (p) => p.twoFactorAuthType.id === TwoFactorVerificationProvider.SMS
            )?.twoFactorVerificationProvider.id === PhoneVerificationProvider.VONAGE,
          twilio:
            countryPreference.twoFactorAuthVerificationProviders.find(
              (p) => p.twoFactorAuthType.id === TwoFactorVerificationProvider.SMS
            )?.twoFactorVerificationProvider.id === PhoneVerificationProvider.TWILIO,
        },
      },
    }
  },

  handleSubmit: async (values, { props, setSubmitting }) => {
    try {
      await props.onSubmit(dataToSubmit(values))
      setSubmitting(false)
    } catch (e) {
      console.error(e)
      setSubmitting(false)
    }
  },

  validate: (values) => {
    const errors: FormikErrors<CountryPreferencesFormValues> = {}

    if (!values.currencyId) {
      errors.currencyId = 'Required'
    }
    if (!values.languageId) {
      errors.languageId = 'Required'
    }
    if (!values.countryId) {
      errors.countryId = 'Required'
    }
    if (!JiraTicketPattern.test(values.jiraTicketNumber ?? '')) {
      errors.jiraTicketNumber = 'Invalid Format, correct format is ABC-12345'
    }
    if (!values.jiraTicketNumber) {
      errors.jiraTicketNumber = 'Required'
    }

    return errors
  },
})(EditCountryFormUI)
